import type { ParticipantBehaviour, TimeSeriesChartWidget } from '@piccolohealth/pbs-common';
import { ChakraV3, Empty, Spin } from '@piccolohealth/ui';
import { type DateTime, P } from '@piccolohealth/util';
import React from 'react';

import { createModal } from '../../../../components/generic/Modal';
import { useParticipantBehavioursRecordingsQuery } from '../../../../graphql/hooks/useParticipantQuery';
import { useAppContext } from '../../../../hooks/useAppContext';
import { useTimeseriesChart } from '../../hooks/useTimeseriesChart';
import { ParticipantWidgetTimeRangeControl } from '../ParticipantWidgetTimeRangeControl';
import { TimeSeriesChartWidgetContent } from './TimeSeriesChartWidgetContent';

interface Props {
  participantId: string;
  widget: TimeSeriesChartWidget;
  startDate: DateTime;
  endDate: DateTime;
}

export const TimeSeriesWidgetViewModal = createModal<Props>((props) => {
  const { modal, participantId, widget } = props;
  const { visible, hide, remove } = modal;
  const { organization } = useAppContext();
  const [startDate, setStartDate] = React.useState(props.startDate);
  const [endDate, setEndState] = React.useState(props.endDate);

  const onStartDateChange = (date: DateTime) => {
    setStartDate(date);
  };

  const onEndDateChange = (date: DateTime) => {
    setEndState(date);
  };

  const participantBehaviourQuery = useParticipantBehavioursRecordingsQuery({
    organizationId: organization.id,
    participantId,
  });

  const participantBehaviours =
    (participantBehaviourQuery.data?.organization?.participant
      ?.behaviours as ParticipantBehaviour[]) ?? [];

  const chart = useTimeseriesChart({
    participantBehaviours,
    widget,
    startDate,
    endDate,
    setWidget: P.noop,
    onStartDateChange,
    onEndDateChange,
  });

  const content = P.run(() => {
    if (participantBehaviourQuery.isLoading) {
      return <Spin />;
    }

    if (!participantBehaviours) {
      return <Empty />;
    }

    return (
      <ChakraV3.Stack w='full' h='full'>
        <ChakraV3.FormField label='Date'>
          <ParticipantWidgetTimeRangeControl
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={onStartDateChange}
            onEndDateChange={onEndDateChange}
          />
        </ChakraV3.FormField>
        <TimeSeriesChartWidgetContent chart={chart} />
      </ChakraV3.Stack>
    );
  });

  return (
    <ChakraV3.Dialog.Root
      open={visible}
      onOpenChange={hide}
      onExitComplete={remove}
      size='cover'
      scrollBehavior='inside'
    >
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <ChakraV3.Dialog.Content>
          <ChakraV3.DialogTitleSubtitle title={widget.title} />
          <ChakraV3.Dialog.Body>{content}</ChakraV3.Dialog.Body>
          <ChakraV3.Dialog.Footer>
            <ChakraV3.Button onClick={hide} size='sm' colorPalette='brand'>
              Close
            </ChakraV3.Button>
          </ChakraV3.Dialog.Footer>
        </ChakraV3.Dialog.Content>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
